'use client';
import clsx from 'clsx';
import { useRef, useCallback, useEffect } from 'react';

function useCoachmarkExternalTarget({
  targetSelector,
  targetElementId,
  disabled = false,
  toggleOnExternalClick = true,
  onToggle,
  onExternalClick,
  wrapperClassName
}) {
  const wrapperElRef = useRef(null);
  const createdWrapperRef = useRef(false);
  const originalParentRef = useRef(null);
  const originalNextSiblingRef = useRef(null);
  const movedElementRef = useRef(null);
  const anchorSpanRef = useRef(null);
  const resolveTarget = useCallback(() => {
    if (typeof document === "undefined") return null;
    if (targetSelector) {
      try {
        const match = document.querySelector(targetSelector);
        return match || null;
      } catch {
        return null;
      }
    }
    if (targetElementId) {
      return document.getElementById(targetElementId) || null;
    }
    return null;
  }, [targetSelector, targetElementId]);
  useEffect(() => {
    const el = resolveTarget();
    if (!el) return;
    if (el.parentElement && el.parentElement.classList.contains("kohler-coachmark-target")) {
      wrapperElRef.current = el.parentElement;
      createdWrapperRef.current = false;
    } else {
      movedElementRef.current = el;
      originalParentRef.current = el.parentNode;
      originalNextSiblingRef.current = el.nextSibling;
      const wrapper2 = document.createElement("button");
      wrapper2.type = "button";
      wrapper2.className = clsx(wrapperClassName, "kohler-coachmark-target");
      wrapper2.setAttribute("aria-haspopup", "dialog");
      originalParentRef.current?.insertBefore(wrapper2, el);
      wrapper2.appendChild(el);
      wrapperElRef.current = wrapper2;
      createdWrapperRef.current = true;
    }
    const wrapper = wrapperElRef.current;
    if (wrapper && toggleOnExternalClick) {
      const onClick = (e) => {
        if (disabled) return;
        onToggle();
        onExternalClick?.(e);
      };
      wrapper.addEventListener("click", onClick);
      return () => {
        wrapper.removeEventListener("click", onClick);
        if (createdWrapperRef.current) {
          const moved = movedElementRef.current;
          const parent = originalParentRef.current;
          if (moved && parent) {
            if (originalNextSiblingRef.current) {
              parent.insertBefore(moved, originalNextSiblingRef.current);
            } else {
              parent.appendChild(moved);
            }
          }
          if (wrapper.parentNode) wrapper.remove();
        }
        wrapperElRef.current = null;
        createdWrapperRef.current = false;
        movedElementRef.current = null;
        originalParentRef.current = null;
        originalNextSiblingRef.current = null;
      };
    }
    return () => {
      if (createdWrapperRef.current) {
        const wrapper2 = wrapperElRef.current;
        const moved = movedElementRef.current;
        const parent = originalParentRef.current;
        if (moved && parent) {
          if (originalNextSiblingRef.current) {
            parent.insertBefore(moved, originalNextSiblingRef.current);
          } else {
            parent.appendChild(moved);
          }
        }
        if (wrapper2 && wrapper2.parentNode) wrapper2.remove();
      }
      wrapperElRef.current = null;
      createdWrapperRef.current = false;
      movedElementRef.current = null;
      originalParentRef.current = null;
      originalNextSiblingRef.current = null;
    };
  }, [resolveTarget, disabled, toggleOnExternalClick, onToggle, onExternalClick, wrapperClassName]);
  useEffect(() => {
    const wrapper = wrapperElRef.current;
    const span = anchorSpanRef.current;
    if (wrapper && span && span.parentElement !== wrapper) {
      wrapper.appendChild(span);
    }
  });
  return {
    isActive: !!wrapperElRef.current,
    anchorSpanRef
  };
}

export { useCoachmarkExternalTarget };
